import React, { useEffect, useState } from 'react';
import logoImage from '../assets/logo.png';
import cachet from '../assets/cachet.png';
import { formatLibelleForDisplay } from '../utils/libelleFormatter';

import { authJsonHeaders } from '../utils/authHeaders';
import { shouldShowCaisseCarryLine } from '../utils/caisseActiveYear';

function previousCaisseClosingDate({ filterType, date, mois, annee, dateDebut }) {
  if (filterType === 'jour' && date) {
    const parts = String(date).slice(0, 10).split('-').map(Number);
    if (parts.length === 3 && !parts.some(Number.isNaN)) {
      const d = new Date(parts[0], parts[1] - 1, parts[2] - 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  }
  if (filterType === 'mois' && mois && annee) {
    const year = parseInt(annee, 10);
    const month = parseInt(mois, 10);
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const lastDayPrevMonth = new Date(prevYear, prevMonth, 0).getDate();
    return `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(lastDayPrevMonth).padStart(2, '0')}`;
  }
  if (filterType === 'annee' && annee) {
    const prevYear = parseInt(annee, 10) - 1;
    return `${prevYear}-12-31`;
  }
  if (filterType === 'periode' && dateDebut) {
    const parts = String(dateDebut).slice(0, 10).split('-').map(Number);
    if (parts.length === 3 && !parts.some(Number.isNaN)) {
      const d = new Date(parts[0], parts[1] - 1, parts[2] - 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  }
  return '';
}

function periodStartCaisseDate({ filterType, date, mois, annee, dateDebut }) {
  if (filterType === 'jour' && date) return String(date).slice(0, 10);
  if (filterType === 'mois' && mois && annee) {
    return `${annee}-${String(mois).padStart(2, '0')}-01`;
  }
  if (filterType === 'annee' && annee) return `${annee}-01-01`;
  if (filterType === 'periode' && dateDebut) return String(dateDebut).slice(0, 10);
  return '';
}

function apiFetch(url, options = {}) {
  return fetch(url, {
    ...options,
    headers: {
      ...authJsonHeaders(),
      ...(options.headers || {}),
    },
  });
}

export default function EtatPrintPage({ mode = 'client' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [honoraires, setHonoraires] = useState([]);
  const [depenses, setDepenses] = useState([]);
  const [chargesPayeesParFondClient, setChargesPayeesParFondClient] = useState([]);
  const [clientName, setClientName] = useState('');
  const [soldeReporte, setSoldeReporte] = useState(null);
  const [soldeCaisseCgm, setSoldeCaisseCgm] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filterType = params.get('filterType');
    const date = params.get('date');
    const mois = params.get('mois');
    const annee = params.get('annee');
    const dateDebut = params.get('dateDebut');
    const dateFin = params.get('dateFin');
    const beneficiaire = params.get('beneficiaire');
    const clientId = params.get('client_id');

    const run = async () => {
      try {
        // Vérifier le type depuis les paramètres URL
        const typeParam = params.get('type');
        const isFondClientCgm = typeParam === 'fond-client-cgm';
        
        if (mode === 'client' && !clientId && !isFondClientCgm) {
          setError('client_id requis');
          setLoading(false);
          return;
        }

        // Optional: fetch client name
        let clientNameLocal = '';
        let soldeReporteLocal = null;
        if (mode === 'client' && clientId) {
          try {
            const r = await apiFetch(`/api/clients/${clientId}`);
            const d = await r.json();
            if (d.success && d.client) {
              clientNameLocal = `${d.client.nom || ''} ${d.client.prenom || ''}`.trim();
              setClientName(clientNameLocal);
            }
            // solde reporté - utiliser l'année de la période sélectionnée
            let anneePourSolde = new Date().getFullYear();
            if (annee) {
              anneePourSolde = parseInt(annee);
            } else if (date) {
              anneePourSolde = new Date(date).getFullYear();
            } else if (dateDebut) {
              anneePourSolde = new Date(dateDebut).getFullYear();
            } else if (mois) {
              // Si mois est fourni sans annee, utiliser l'année courante
              anneePourSolde = new Date().getFullYear();
            }
            const sr = await apiFetch(`/api/charges-mensuelles/solde-reporte/${clientId}?annee=${anneePourSolde}`);
            const srd = await sr.json();
            if (srd.success) {
              soldeReporteLocal = srd;
              setSoldeReporte(srd);
            }
          } catch (_) { }
        }

        const honorairesData = await fetchHonoraires({ filterType, date, mois, annee, dateDebut, dateFin, beneficiaire, clientId });
        setHonoraires(honorairesData);
        const depensesData = await fetchDepenses({ filterType, date, mois, annee, dateDebut, dateFin, clientId });
        setDepenses(depensesData);
        
        // Récupérer les charges payées par fond client si c'est pour fond client chez CGM
        let chargesPayeesData = [];
        if (isFondClientCgm) {
            chargesPayeesData = await fetchChargesPayeesParFondClient({ filterType, date, mois, annee, dateDebut, dateFin });
            setChargesPayeesParFondClient(chargesPayeesData);
        }

        // Récupérer le solde de la caisse CGM si c'est pour l'état bureau
        let soldeCaisseCgmLocal = 0;
        let soldeDebutJourneeLocal = 0;
        if (mode === 'bureau') {
            try {
                // Déterminer la date de référence du solde d'ouverture.
                // On interroge le jour sélectionné pour calculer le solde avant 00:00 de ce jour,
                // puis on affiche la clôture de la veille dans le libellé "Montant Caisse".
                let dateForSoldeDebut = null;
                if (filterType === 'jour' && date) {
                    dateForSoldeDebut = date;
                } else if (filterType === 'mois' && mois && annee) {
                    const m = String(mois).padStart(2, '0');
                    dateForSoldeDebut = `${annee}-${m}-01`;
                } else if (filterType === 'annee' && annee) {
                    dateForSoldeDebut = `${annee}-01-01`;
                } else if (filterType === 'periode' && dateDebut) {
                    dateForSoldeDebut = dateDebut;
                }

                if (dateForSoldeDebut) {
                    // Tous les filtres utilisent maintenant /reste-cumule pour le solde d'ouverture
                    const soldeDebutResponse = await apiFetch(`/api/caisse-cgm/reste-cumule?date=${dateForSoldeDebut}`);
                    const soldeDebutData = await soldeDebutResponse.json();
                    if (soldeDebutData.success) {
                        soldeDebutJourneeLocal = soldeDebutData.reste_cgm ?? 0;
                    } else {
                        console.error('Erreur API reste-cumule:', soldeDebutData.error);
                        soldeDebutJourneeLocal = 0;
                    }
                }
                
                const soldeResponse = await apiFetch('/api/caisse-cgm/solde');
                const soldeData = await soldeResponse.json();
                if (soldeData.success) {
                    soldeCaisseCgmLocal = soldeData.solde_actuel || 0;
                    setSoldeCaisseCgm(soldeCaisseCgmLocal);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du solde de la caisse CGM:', error);
            }
        }

        setLoading(false);

        setTimeout(() => handlePrint({ honoraires: honorairesData, depenses: depensesData, chargesPayeesParFondClient: chargesPayeesData, soldeCaisseCgm: soldeCaisseCgmLocal, soldeDebutJournee: soldeDebutJourneeLocal, filterType, date, mois, annee, dateDebut, dateFin, beneficiaire, clientName: (mode === 'client' ? (clientNameLocal || clientName) : ''), soldeReporte: soldeReporteLocal || soldeReporte, mode }), 0);
      } catch (e) {
        setError(e.message || 'Erreur');
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const formatMontant = (montant) => {
    const value = parseFloat(montant);
    if (isNaN(value)) return '0,000 TND';
    
    // Gérer les nombres négatifs
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    
    // Tronquer au lieu d'arrondir pour préserver les valeurs exactes
    // Multiplier par 1000, tronquer avec Math.floor, puis diviser par 1000
    // Utiliser Math.floor pour éviter les erreurs d'arrondissement de JavaScript
    const montantTronque = Math.floor(absValue * 1000 + 0.0000001) / 1000; // +0.0000001 pour gérer les erreurs de précision flottante
    
    // Convertir en chaîne avec toFixed pour avoir une représentation précise
    // puis extraire manuellement les 3 premières décimales pour éviter l'arrondissement
    const tempStr = montantTronque.toFixed(10); // Utiliser plus de décimales pour éviter les erreurs
    const parts = tempStr.split('.');
    let integerPart = parts[0] || '0';
    let decimalPart = parts[1] ? parts[1].substring(0, 3) : '000'; // Tronquer à 3 décimales
    
    // S'assurer que la partie décimale a exactement 3 chiffres
    if (decimalPart.length < 3) {
      decimalPart = decimalPart.padEnd(3, '0');
    }
    
    // Formater la partie entière avec des espaces comme séparateurs de milliers
    const integerFormatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    // Utiliser le format français avec virgule comme séparateur décimal
    return `${isNegative ? '-' : ''}${integerFormatted},${decimalPart} TND`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const parts = dateStr.slice(0, 10).split('-');
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    const d = new Date(dateStr);
    if (isNaN(d)) return String(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${day}/${m}/${y}`;
  };

  async function fetchHonoraires({ filterType, date, mois, annee, dateDebut, dateFin, beneficiaire, clientId }) {
    // Vérifier si c'est pour fond client chez CGM
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');
    const isFondClientCgm = typeParam === 'fond-client-cgm';
    
    // Pour fond client chez CGM, utiliser l'endpoint optimisé
    if (isFondClientCgm) {
      let url = '/api/charges-mensuelles/fond-client-cgm?';
      const params = new URLSearchParams();
      
      if (filterType === 'jour' && date) {
        params.append('date', date);
      } else if (filterType === 'periode' && dateDebut && dateFin) {
        params.append('dateDebut', dateDebut);
        params.append('dateFin', dateFin);
      } else if (filterType === 'mois' && mois && annee) {
        params.append('mois', mois);
        params.append('annee', annee);
      } else if (filterType === 'annee' && annee) {
        params.append('annee', annee);
      }
      
      const r = await apiFetch(url + params.toString());
      const d = await r.json();
      
      if (d.success && d.charges) {
        // Convertir les charges en format honoraires pour l'affichage
        return d.charges.map(charge => ({
          id: `charge_${charge.id}`,
          date: charge.date,
          libelle: charge.libelle,
          montant: parseFloat(charge.avance || 0),
          avance: parseFloat(charge.avance || 0),
          client_nom: charge.client_nom || '',
          client_prenom: charge.client_prenom || ''
        }));
      }
      return [];
    }
    
    // Pour les autres types, utiliser l'endpoint normal
    let url = '/api/honoraires';
    const params = new URLSearchParams();
    if (mode !== 'bureau') params.append('type', mode);
    if (clientId) params.append('client_id', clientId);
    if (filterType === 'jour' && date) params.append('date', date);
    else if (filterType === 'periode' && dateDebut && dateFin) { params.append('date_debut', dateDebut); params.append('date_fin', dateFin); }
    else if (filterType === 'mois' && mois && annee) { params.append('mois', mois); params.append('annee', annee); }
    else if (filterType === 'annee' && annee) params.append('annee', annee);
    if (params.toString()) url += `?${params.toString()}`;

    const r = await apiFetch(url);
    const d = await r.json();
    let data = d.success ? (d.honoraires || []) : [];

    // Garder seulement :
    // - Etat client : HONORAIRES REÇU + HONORAIRES PLATEFORME TEJ + AVANCE
    // - Etat CGM (bureau) : uniquement HONORAIRES REÇU + HONORAIRES PLATEFORME TEJ
    data = data.filter(h => {
      const lib = (h.libelle || '').toUpperCase();
      const isHonBase = lib.includes('HONORAIRES REÇU') || lib.includes('HONORAIRES RECU');
      const isHonTej = lib.includes('HONORAIRES PLATEFORME TEJ');
      const isAvance = lib.includes('AVANCE');
      const isHon = mode === 'client' ? (isHonBase || isHonTej || isAvance) : (isHonBase || isHonTej);
      const amt = parseFloat(h.avance || h.montant || 0);
      return isHon && amt > 0;
    });

    // Pour l'état CGM (bureau) : ajouter les dépôts de la caisse CGM (même logique que le modal Rechercher)
    if (mode === 'bureau') {
      try {
        let depotsUrl = '/api/caisse-cgm/depots?';
        const depotsParams = new URLSearchParams();
        if (filterType === 'jour' && date) {
          depotsParams.append('date', date);
        } else if (filterType === 'periode' && dateDebut && dateFin) {
          depotsParams.append('date_debut', dateDebut);
          depotsParams.append('date_fin', dateFin);
        } else if (filterType === 'mois' && mois && annee) {
          depotsParams.append('mois', mois);
          depotsParams.append('annee', annee);
        } else if (filterType === 'annee' && annee) {
          depotsParams.append('annee', annee);
        } else {
          depotsParams.append('annee', new Date().getFullYear());
        }
        depotsUrl += depotsParams.toString();
        const depotsResponse = await apiFetch(depotsUrl);
        const depotsJson = await depotsResponse.json();
        if (depotsJson.success && Array.isArray(depotsJson.depots) && depotsJson.depots.length > 0) {
          const depotsAsHonoraires = depotsJson.depots.map(d => ({
            id: `depot_${d.id}`,
            date: d.created_at,
            libelle: (d.commentaire && d.commentaire.trim()) ? `Dépôt Caisse CGM - ${d.commentaire.trim()}` : 'Dépôt Caisse CGM',
            client_nom: '',
            client_prenom: '',
            montant: parseFloat(d.montant || 0),
            avance: 0
          }));
          data = [...data, ...depotsAsHonoraires];
          data.sort((a, b) => new Date(a.date) - new Date(b.date));
        }
      } catch (depotsErr) {
        console.error('Erreur lors de la récupération des dépôts caisse CGM (print):', depotsErr);
      }
    }

    data.sort((a, b) => new Date(a.date) - new Date(b.date));
    return data;
  }

  async function fetchChargesPayeesParFondClient({ filterType, date, mois, annee, dateDebut, dateFin }) {
    let url = '/api/charges-mensuelles/paye-par-fond-client?';
    const params = new URLSearchParams();
    
    if (filterType === 'jour' && date) {
      params.append('date', date);
    } else if (filterType === 'periode' && dateDebut && dateFin) {
      params.append('dateDebut', dateDebut);
      params.append('dateFin', dateFin);
    } else if (filterType === 'mois' && mois && annee) {
      params.append('mois', mois);
      params.append('annee', annee);
    } else if (filterType === 'annee' && annee) {
      params.append('annee', annee);
    }
    
    const r = await apiFetch(url + params.toString());
    const d = await r.json();
    
    if (d.success && d.charges) {
      return d.charges.map(charge => ({
        id: `charge_paye_${charge.id}`,
        date: charge.date,
        libelle: charge.libelle,
        montant: parseFloat(charge.montant || 0),
        avance: parseFloat(charge.avance || 0),
        client_nom: charge.client_nom || '',
        client_prenom: charge.client_prenom || ''
      }));
    }
    return [];
  }

  async function fetchDepenses({ filterType, date, mois, annee, dateDebut, dateFin, clientId }) {
    // Pour fond client chez CGM, ne pas récupérer les dépenses
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');
    if (typeParam === 'fond-client-cgm') {
      return [];
    }
    
    if (mode === 'bureau') {
      // Pour l'état CGM (bureau), récupérer :
      // 1. Toutes les dépenses bureau
      // 2. Les dépenses client avec préfixe [CGM] (payées par CGM)
      
      // Construire les URLs pour les deux types
      let bureauUrl = '/api/depenses/bureau';
      let clientCgmUrl = '/api/depenses';
      const clientCgmParams = new URLSearchParams();
      
      if (filterType === 'jour' && date) {
        bureauUrl = '/api/depenses/bureau/par-periode';
        bureauUrl += `?date_debut=${date}&date_fin=${date}`;
        clientCgmUrl = '/api/depenses/par-periode';
        clientCgmParams.append('date_debut', date);
        clientCgmParams.append('date_fin', date);
      } else if (filterType === 'periode' && dateDebut && dateFin) {
        bureauUrl = '/api/depenses/bureau/par-periode';
        bureauUrl += `?date_debut=${dateDebut}&date_fin=${dateFin}`;
        clientCgmUrl = '/api/depenses/par-periode';
        clientCgmParams.append('date_debut', dateDebut);
        clientCgmParams.append('date_fin', dateFin);
      } else if (filterType === 'mois' && mois && annee) {
        const start = `${annee}-${String(mois).padStart(2, '0')}-01`;
        const endDate = new Date(annee, mois, 0);
        const end = `${annee}-${String(mois).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
        bureauUrl = '/api/depenses/bureau/par-periode';
        bureauUrl += `?date_debut=${start}&date_fin=${end}`;
        clientCgmUrl = '/api/depenses/par-periode';
        clientCgmParams.append('date_debut', start);
        clientCgmParams.append('date_fin', end);
      } else if (filterType === 'annee' && annee) {
        const start = `${annee}-01-01`;
        const end = `${annee}-12-31`;
        bureauUrl = '/api/depenses/bureau/par-periode';
        bureauUrl += `?date_debut=${start}&date_fin=${end}`;
        clientCgmUrl = '/api/depenses/par-periode';
        clientCgmParams.append('date_debut', start);
        clientCgmParams.append('date_fin', end);
      }
      
      if (clientCgmParams.toString()) {
        clientCgmUrl += `?${clientCgmParams.toString()}`;
      }
      
      // Récupérer les deux types en parallèle
      const [bureauRes, clientCgmRes] = await Promise.all([
        apiFetch(bureauUrl),
        apiFetch(clientCgmUrl)
      ]);
      
      const bureauData = await bureauRes.json();
      const clientCgmData = await clientCgmRes.json();
      
      let depensesBureau = bureauData.success ? (bureauData.depenses || []) : [];
      let depensesClientCgm = clientCgmData.success ? (clientCgmData.depenses || []) : [];
      
      // Filtrer les dépenses client pour ne garder que celles avec [CGM]
      depensesClientCgm = depensesClientCgm.filter(dep => {
        const desc = (dep.description || dep.libelle || '').toUpperCase();
        return desc.includes('[CGM]');
      });
      
      // Combiner et exclure les honoraires reçus
      let data = [...depensesBureau, ...depensesClientCgm];
      data = data.filter(dep => {
        const desc = (dep.description || dep.libelle || '').toUpperCase();
        const isHonoraireRecu = desc.includes('HONORAIRES REÇU') || desc.includes('HONORAIRES RECU');
        const isAvanceDeclaration = desc.includes('AVANCE');
        return !isHonoraireRecu && !isAvanceDeclaration;
      });

      // Ajouter les retraits de la caisse CGM (affichés et soustraits du Reste CGM)
      try {
        let retraitsUrl = '/api/caisse-cgm/retraits?';
        const retraitsParams = new URLSearchParams();
        if (filterType === 'jour' && date) {
          retraitsParams.append('date', date);
        } else if (filterType === 'periode' && dateDebut && dateFin) {
          retraitsParams.append('date_debut', dateDebut);
          retraitsParams.append('date_fin', dateFin);
        } else if (filterType === 'mois' && mois && annee) {
          retraitsParams.append('mois', mois);
          retraitsParams.append('annee', annee);
        } else if (filterType === 'annee' && annee) {
          retraitsParams.append('annee', annee);
        } else {
          retraitsParams.append('annee', new Date().getFullYear());
        }
        retraitsUrl += retraitsParams.toString();
        const retraitsRes = await apiFetch(retraitsUrl);
        const retraitsJson = await retraitsRes.json();
        if (retraitsJson.success && Array.isArray(retraitsJson.retraits) && retraitsJson.retraits.length > 0) {
          const toDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
          const sameAmount = (a, b) => Math.abs(parseFloat(a || 0) - parseFloat(b || 0)) < 0.01;
          const retraitsAsDepenses = retraitsJson.retraits
            .filter(r => {
              const isCashDrop =
                r.source_type === 'cash_drop' ||
                r.source_type === 'caisse_operation' ||
                r.type_retrait != null ||
                r.retrait_type != null;
              const rDay = toDay(r.date || r.created_at);
              const rMontant = parseFloat(r.montant || 0);
              const rComment = (r.commentaire || '').trim();
              if (!isCashDrop && !rComment) return false;
              const alreadyExists = data.some(d => {
                if (String(d.id) === `retrait_caisse_${r.id}` || String(d.id) === `cash_drop_${r.id}`) return true;
                if (!sameAmount(d.montant, rMontant)) return false;
                if (toDay(d.date) !== rDay) return false;
                const dLib = (d.libelle || d.description || '').trim();
                if (rComment && dLib && dLib === rComment) return true;
                if (rComment.toUpperCase().startsWith('BUREAU')) {
                  const dLibU = dLib.toUpperCase();
                  if (!dLibU.includes('[CGM]')) return false;
                  const benef = (d.nom_beneficiaire || d.client || d.beneficiaire || '').trim().toUpperCase();
                  const partAfterBureau = rComment.replace(/^BUREAU\s*-\s*/i, '').toUpperCase();
                  return benef && partAfterBureau.includes(benef);
                }
                return false;
              });
              return !alreadyExists;
            })
            .map(r => {
              const isBureau =
                String(r.type_retrait || '').toLowerCase() === 'bureau' ||
                String(r.retrait_type || '').toLowerCase() === 'bureau' ||
                Number(r.type_retrait) === 1 ||
                r.is_bureau;
              const isInventaire =
                String(r.type_retrait || '').toLowerCase() === 'inventaire' ||
                String(r.retrait_type || '').toLowerCase() === 'inventaire';
              const note = (r.commentaire && r.commentaire.trim())
                ? r.commentaire.trim()
                : (isBureau ? 'Retrait caisse bureau' : isInventaire ? 'Retrait inventaire' : 'Retrait caisse');
              return {
                id: `retrait_caisse_${r.id}`,
                date: r.date || r.created_at,
                description: note,
                libelle: note,
                montant: parseFloat(r.montant || 0),
                beneficiaire: isBureau ? 'Caisse CGM (Bureau)' : isInventaire ? 'Caisse CGM (Inventaire)' : 'Caisse CGM',
              };
            });
          data = [...data, ...retraitsAsDepenses];
          data.sort((a, b) => new Date(a.date) - new Date(b.date));
        }
      } catch (retraitsErr) {
        console.error('Erreur lors de la récupération des retraits caisse CGM (print):', retraitsErr);
      }

      return data;
    } else {
      // Pour les dépenses client, récupérer normalement
      let url = '/api/depenses';
      const params = new URLSearchParams();
      params.append('type', mode);
      if (clientId) params.append('client_id', clientId);
      if (filterType === 'jour' && date) { url = '/api/depenses/par-periode'; params.append('date_debut', date); params.append('date_fin', date); }
      else if (filterType === 'periode' && dateDebut && dateFin) { url = '/api/depenses/par-periode'; params.append('date_debut', dateDebut); params.append('date_fin', dateFin); }
      else if (filterType === 'mois' && mois && annee) { const start = `${annee}-${String(mois).padStart(2, '0')}-01`; const endDate = new Date(annee, mois, 0); const end = `${annee}-${String(mois).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`; url = '/api/depenses/par-periode'; params.append('date_debut', start); params.append('date_fin', end); }
      else if (filterType === 'annee' && annee) { url = '/api/depenses/par-periode'; params.append('date_debut', `${annee}-01-01`); params.append('date_fin', `${annee}-12-31`); }
      if (params.toString()) url += `?${params.toString()}`;

      const r = await apiFetch(url);
      const d = await r.json();
      let data = d.success ? (d.depenses || []) : [];

      // Inclure toutes les dépenses (y compris celles payées par CGM)
      // Le libellé [CGM] sera remplacé par [PAYÉ PAR CGM] dans l'affichage
      return data;
    }
  }

  const getCurrentDate = (filterType, { date, mois, annee, dateDebut, dateFin }) => {
    if ((filterType === 'jour' && dateDebut && dateFin) || filterType === 'periode') return `${dateDebut} - ${dateFin}`;
    if (filterType === 'mois' && mois && annee) return `${String(mois).padStart(2, '0')}/${annee}`;
    if (filterType === 'annee' && annee) return `${annee}`;
    const now = new Date();
    return formatDate(now.toISOString());
  };

  const getTotalMontant = (arr) => {
    // Pour les honoraires reçus, utiliser GREATEST(avance, montant) comme dans le backend
    // Pour les honoraires reçus, avance contient souvent le montant reçu
    return arr.reduce((t, h) => {
      const avanceValue = parseFloat(h.avance || 0);
      const montantValue = parseFloat(h.montant || 0);
      // Utiliser le maximum entre avance et montant (comme dans les routes backend)
      const amount = Math.max(avanceValue, montantValue);
      return t + amount;
    }, 0);
  };
  const getTotalDepenses = (arr) => arr.reduce((t, d) => t + parseFloat(d.montant || 0), 0);
  const getTotalPayeParFondClient = (arr) => arr.reduce((t, c) => t + parseFloat(c.montant || 0), 0);
  const getSoldeInitialCaisseCgm = (soldeActuel, honorairesArr, depensesArr) => {
    const totalHonoraires = getTotalMontant(honorairesArr);
    const totalDepenses = getTotalDepenses(depensesArr);
    // Solde initial = solde actuel - honoraires de la période + dépenses de la période
    return soldeActuel - totalHonoraires + totalDepenses;
  };

  const getResteCaisseCgmFinal = (soldeActuel, soldeDebut, honorairesArr, depensesArr, filterTypeParam) => {
    // Pour les filtres jour / mois / période / année :
    // Reste CGM = solde début période + honoraires de la période - dépenses de la période
    if (['jour', 'mois', 'periode', 'annee'].includes(filterTypeParam)) {
      const totalHonoraires = getTotalMontant(honorairesArr);
      const totalDepenses = getTotalDepenses(depensesArr);
      return soldeDebut + totalHonoraires - totalDepenses;
    }
    // Pour les autres filtres, retourner le solde actuel
    return soldeActuel;
  };

  // Fonction pour formater le libellé pour l'affichage dans l'état client
  const formatLibelleForEtat = (libelle) => {
    if (!libelle) return '-';
    const libelleUpper = libelle.toUpperCase();
    
    // Vérifier si c'est HONORAIRES PLATEFORME TEJ (vérifier en premier car il contient "HONORAIRES")
    if (libelleUpper.includes('HONORAIRES PLATEFORME TEJ')) {
      return 'HONORAIRES REÇU TEJ';
    }
    
    // Vérifier si c'est HONORAIRES REÇU (incluant CABINET)
    if (libelleUpper.includes('HONORAIRES REÇU') || 
        libelleUpper.includes('HONORAIRES RECU') || 
        libelleUpper.includes('HONORAIRE RECU') || 
        libelleUpper.includes('HONORAIRE REÇU')) {
      return 'HONORAIRES REÇU CABINET';
    }
    
    // Utiliser la fonction formatLibelleForDisplay pour les autres cas
    return formatLibelleForDisplay(libelle);
  };

  function handlePrint(ctx) {
    let { honoraires, depenses, chargesPayeesParFondClient = [], soldeCaisseCgm = 0, soldeDebutJournee = 0, filterType, date, mois, annee, dateDebut, dateFin, clientName, soldeReporte, mode } = ctx;
    const prevClose = previousCaisseClosingDate({ filterType, date, mois, annee, dateDebut });
    const periodStart = periodStartCaisseDate({ filterType, date, mois, annee, dateDebut });
    const showCaisseCarry = mode === 'bureau' && shouldShowCaisseCarryLine(prevClose, periodStart, filterType);
    const caisseCarry = showCaisseCarry ? (parseFloat(soldeDebutJournee) || 0) : 0;
    
    // Vérifier si c'est pour fond client chez CGM
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');
    const isFondClientCgm = typeParam === 'fond-client-cgm';

    // Même filtre que EtatCgmModal (client) : dépenses liées aux charges + exclure carte bancaire
    if (mode === 'client' && !isFondClientCgm) {
      depenses = (depenses || []).filter((depense) => {
        const libelleText = (depense.description || depense.libelle || '').toUpperCase();
        if (libelleText.includes('[CARTE BANCAIRE]') || libelleText.includes('[CARTE]')) return false;
        return depense.charge_id !== null && depense.charge_id !== undefined;
      });
    }
    
    // Obtenir la date et l'heure d'impression
    const now = new Date();
    const dateImpression = now.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const heureImpression = now.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const infoGauche = `
    Matricule fiscale: 1408941/H/A/P/000
    <br>Banque Zitouna Agence Ben Arous
    <br>RIB : 25047000000039870129
`;
    const infoDroit = `
    Membre de la compagnie des comptables de Tunisie.
    <br>62 Avenue de France, bureau N°3, 1er étage 2013 Ben Arous.
    <br>(+216) 79 410 028
    <br>contact@ccgm.com.tn
`;

    // Aligner sur le modal "Rechercher" :
    // - Pour jour/mois/période/année : Reste CGM = solde début période + honoraires - dépenses
    // - Sinon : Reste CGM = honoraires - dépenses
    const totalHonorairesCalc = getTotalMontant(honoraires);
    const totalDepensesCalc = getTotalDepenses(depenses);
    const shouldAddCaisse =
      mode === 'bureau' && ['jour', 'mois', 'periode', 'annee'].includes(filterType || '');
    const resteCgmBureau = mode === 'bureau'
      ? (shouldAddCaisse
          ? (caisseCarry + totalHonorairesCalc - totalDepensesCalc)
          : totalHonorairesCalc - totalDepensesCalc)
      : null;
    // Client : aligné sur EtatCgmModal.getResteCgm() = total honoraires/avances − dépenses (pas caisse globale)
    const montantFinalCalc = mode === 'bureau'
      ? resteCgmBureau
      : totalHonorairesCalc - totalDepensesCalc;

    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Synthèse ${mode === 'bureau' ? 'CGM' : 'Client'}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 12px; font-size: 11px; }
    .header { text-align: center; margin-bottom: 10px; }
    .logo-container img { height: 50px; width: auto; margin-bottom: 4px; }
    .header-infos { display: flex; justify-content: space-between; text-align: left; margin-top: 6px; font-size: 9px; line-height: 1.3; }
    .info-gauche { width: 48%; text-align: left; }
    .info-droit { width: 48%; text-align: right; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; border: 1px solid #000; }
    th, td { border: 1px solid #000; padding: 4px 6px; }
    thead th { font-weight: bold; background: #f9f9f9; border: 1px solid #000; }
    .details-section { margin-top: 12px; }
    .total-row { font-weight: bold; background: #f9f9f9; }
    .footer-section { margin-top: 20px; }
    .footer-content { display: flex; justify-content: space-around; align-items: flex-end; border-top: 1px solid #333; padding-top: 12px; margin-top: 12px; }
    .signature-block { text-align: center; }
    .signature-block img { height: 70px; width: auto; object-fit: contain; }
    .signature-line { font-size: 11px; color: #333; margin-top: 4px; }
  </style>
</head>
<body>

  <div style="text-align: right; margin-bottom: 10px; font-size: 11px; font-weight: bold;">
    Date d'impression : ${dateImpression}, ${heureImpression}
  </div>
  
  <div class="header">
    <div class="logo-container">
      <img src="${logoImage}" alt="Logo CGM" />
    </div>
    <div class="header-infos">
      <div class="info-gauche">${infoGauche}</div>
      <div class="info-droit">${infoDroit}</div>
    </div>
  </div>
  <hr>

  ${mode === 'client' ? `<div style="margin-bottom:10px; font-weight:bold; font-size: 14px;">Client: ${clientName || ''}</div>` : ''}

  <div class="details-section">
    <table>
      <thead>
        <tr><th>Date</th><th>Libellé</th>${mode === 'client' ? '' : '<th>Client</th>'}<th>Montant</th></tr>
      </thead>
      <tbody>
        ${(() => {
          const rows = [];

          // Ligne "Montant Caisse" pour l'état bureau filtré par jour / mois / période / année
          const isFilterByDay = filterType === 'jour';
          const isFilterByMonth = filterType === 'mois';
          const isFilterByPeriod = filterType === 'periode';
          const isFilterByYear = filterType === 'annee';
          // Ne pas afficher "Montant Caisse" pour l'état fond client chez CGM
          const urlParamsLocal = new URLSearchParams(window.location.search);
          const typeParamLocal = urlParamsLocal.get('type');
          const isFondClientCgmLocal = typeParamLocal === 'fond-client-cgm';
          if (
            mode === 'bureau' &&
            !isFondClientCgmLocal &&
            showCaisseCarry &&
            (isFilterByDay || isFilterByMonth || isFilterByPeriod || isFilterByYear)
          ) {
            const displayDate = prevClose;
            const displayDateFormatted = formatDate(displayDate);

            rows.push(`
          <tr>
            <td>${formatDate(displayDate)}</td>
            <td><strong>Montant Caisse (${displayDateFormatted})</strong></td>
            ${mode === 'client' ? '' : `<td>CGM</td>`}
            <td>${formatMontant(caisseCarry)}</td>
          </tr>
            `);
          }

          if (honoraires.length > 0) {
            rows.push(...honoraires.map(h => {
          // Pour les honoraires reçus, utiliser GREATEST(avance, montant) comme dans le backend
          const avanceValue = parseFloat(h.avance || 0);
          const montantValue = parseFloat(h.montant || 0);
          const montantAffiche = Math.max(avanceValue, montantValue);
          // Formater le libellé pour afficher HONORAIRES REÇU CABINET ou HONORAIRES REÇU TEJ
          const libelleFormate = formatLibelleForEtat(h.libelle);
          return `
          <tr>
            <td>${formatDate(h.date)}</td>
            <td>${libelleFormate}</td>
            ${mode === 'client' ? '' : `<td>${(h.client_nom || '') + ' ' + (h.client_prenom || '')}</td>`}
            <td>${formatMontant(montantAffiche)}</td>
          </tr>
        `;
        }));
          } else if (rows.length === 0) {
            rows.push(`<tr><td colspan="${mode === 'client' ? '3' : '4'}" style="text-align:center;color:#666;font-style:italic;">Aucun honoraire reçu</td></tr>`);
          }

          return rows.join('');
        })()}
      </tbody>
      <tfoot>
        <tr class="total-row">
          <td colspan="${mode === 'client' ? '2' : '3'}">TOTAL</td>
          <td>${
            formatMontant(
              mode === 'bureau'
                ? (caisseCarry || 0) + getTotalMontant(honoraires)
                : getTotalMontant(honoraires)
            )
          }</td>
        </tr>
      </tfoot>
    </table>
  </div>

  ${isFondClientCgm && chargesPayeesParFondClient.length > 0 ? `
  <div class="details-section">
    <table>
      <thead><tr><th>Date</th><th>Libellé</th><th>Client</th><th>Montant</th></tr></thead>
      <tbody>
        ${chargesPayeesParFondClient.map(c => `
          <tr>
            <td>${formatDate(c.date)}</td>
            <td>${c.libelle || '-'}</td>
            <td>${(c.client_nom || '') + ' ' + (c.client_prenom || '')}</td>
            <td>${formatMontant(c.montant)}</td>
          </tr>
        `).join('')}
      </tbody>
      <tfoot>
        <tr class="total-row"><td colspan="3">Total payé par fond client</td><td>${formatMontant(getTotalPayeParFondClient(chargesPayeesParFondClient))}</td></tr>
      </tfoot>
    </table>
  </div>
  ` : ''}

  ${depenses.length > 0 ? `
  <div class="details-section">
  <table>
    <thead><tr><th>Date</th><th>Libellé</th>${mode === 'client' ? '' : '<th>Client</th>'}<th>Montant</th></tr></thead>
    <tbody>
        ${depenses.map(d => {
            // Récupérer le nom du bénéficiaire selon la logique précédente
            const rawText = (d.description || d.libelle || '').toUpperCase();
            const isCgmDepense = rawText.includes('[CGM]') || rawText.includes('HONORAIRES REÇU');
            
            // Déterminer le nom du client/beneficiaire
            let clientName = '';
            if (isCgmDepense) {
                clientName = d.nom_beneficiaire || d.beneficiaire || d.client || '';
            } else {
                clientName = d.beneficiaire || d.client || '';
            }
            
            // Alternative: utiliser client_nom et client_prenom si disponibles
            const clientFullName = (d.client_nom || '') + ' ' + (d.client_prenom || '').trim();
            if (clientFullName.trim()) {
                clientName = clientFullName;
            }
            
            let libelleText = (d.description || d.libelle || '-');
            // Remplacer [CGM] par [PAYÉ PAR CGM] dans l'affichage
            libelleText = libelleText.replace(/^\[CGM\]\s*/, '[PAYÉ PAR CGM] ');
            
            return `
                <tr>
                    <td>${formatDate(d.date)}</td>
                    <td>${libelleText}</td>
                    ${mode === 'client' ? '' : `<td>${clientName || '-'}</td>`}
                    <td>${formatMontant(d.montant)}</td>
                </tr>
            `;
        }).join('')}
    </tbody>
    <tfoot>
        <tr class="total-row"><td colspan="${mode === 'client' ? '2' : '3'}">TOTAL DÉPENSES</td><td>${formatMontant(getTotalDepenses(depenses))}</td></tr>
    </tfoot>
</table>
  </div>
  ` : ''}

  <div class="details-section">
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>${
            isFondClientCgm
              ? 'Total Fonds Clients chez CGM'
              : (mode === 'bureau' ? 'Total des honoraires et caisse' : 'Total Honoraires et Avances')
          }</th>
          ${isFondClientCgm ? '<th>Total Payé par Fond Client</th>' : ''}
          ${!isFondClientCgm ? '<th>Total Dépenses</th>' : ''}
          <th>Reste CGM</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${getCurrentDate(filterType, { date, mois, annee, dateDebut, dateFin })}</td>
          <td>${
            formatMontant(
              isFondClientCgm
                ? getTotalMontant(honoraires)
                : (mode === 'bureau'
                    ? (caisseCarry || 0) + getTotalMontant(honoraires)
                    : getTotalMontant(honoraires))
            )
          }</td>
          ${isFondClientCgm ? `<td>${formatMontant(getTotalPayeParFondClient(chargesPayeesParFondClient))}</td>` : ''}
          ${!isFondClientCgm ? `<td>${formatMontant(getTotalDepenses(depenses))}</td>` : ''}
          <td>${formatMontant(isFondClientCgm ? (getTotalMontant(honoraires) - getTotalPayeParFondClient(chargesPayeesParFondClient)) : montantFinalCalc)}</td>
        </tr>
        ${soldeReporte && soldeReporte.soldeReporte !== 0 ? `
        <tr><td colspan="${isFondClientCgm ? '3' : '3'}" style="text-align:right;font-weight:bold;">+ Solde reporté (${soldeReporte.anneePrecedente})</td><td>${formatMontant(soldeReporte.soldeReporte)}</td></tr>
        <tr class="total-row"><td colspan="${isFondClientCgm ? '3' : '3'}" style="text-align:right;font-weight:bold;">= Reste CGM Final</td><td>${formatMontant(isFondClientCgm ? (getTotalMontant(honoraires) - getTotalPayeParFondClient(chargesPayeesParFondClient) + (soldeReporte.soldeReporte || 0)) : (montantFinalCalc + (soldeReporte.soldeReporte || 0)))}</td></tr>
        ` : ''}
      </tbody>
    </table>
  </div>

  <div class="footer-section">
    <div class="footer-content">
      <div class="signature-block"><img src="${cachet}" alt="Cachet et Signature" /><div class="signature-line">Cachet et Signature</div></div>
      ${mode === 'client' ? `
        <div class="signature-block">
          <div style="width:200px;height:60px;border-bottom:1px solid #000;margin:0 auto 5px;"></div>
          <div class="signature-line">Signature Client</div>
        </div>
      ` : ''}
      <div class="signature-block"><div style="width:200px;height:60px;border-bottom:1px solid #000;margin:0 auto 5px;"></div><div class="signature-line">Signature Caissier</div></div>
    </div>
  </div>

</body>
</html>`;
    try {
      // Remplacer le contenu de la page actuelle pour éviter le blocage des popups
      document.open();
      document.write(printContent);
      document.close();
      window.onload = () => { window.print(); };
    } catch (_) {
      // Fallback: tenter l'ancienne méthode si le navigateur l'autorise
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(printContent);
        w.document.close();
        w.onload = () => { w.print(); w.close(); };
      }
    }
  }

  if (loading) return <div className="container py-5">Préparation de l'impression...</div>;
  if (error) return <div className="container py-5 text-danger">{error}</div>;
  return null;
}


